// WebhookTriggersPanel — UI för P-104: inbound webhook-triggers.
//
// Flow:
// 1. User klickar "Skapa webhook" → dialog med label-input.
// 2. Submit → createWebhookTrigger() → backend returnerar path + secret.
// 3. Panel visar copy-bana med full URL + signing_secret (en gång).
// 4. Lista nedan visar metadata (label, path, trigger-count) + disable-knapp.
//
// UX-not: signing_secret visas BARA vid create (hashat i DB). User måste
// kopiera direkt.

import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Webhook, Plus, Copy, Check, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import {
  createWebhookTrigger,
  listMyWebhookTriggers,
  disableWebhookTrigger,
  buildWebhookUrl,
  type WebhookTriggerMetadata,
} from '@/lib/webhookTriggerClient';
import { usePlan } from '@/hooks/usePlan';
import { toast } from '@/hooks/use-toast';

interface Props {
  workflowId: string;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
}

export function WebhookTriggersPanel({ workflowId }: Props) {
  const { limits } = usePlan();
  const [triggers, setTriggers] = useState<WebhookTriggerMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newWebhook, setNewWebhook] = useState<{ url: string; secret: string; label: string } | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = await listMyWebhookTriggers(workflowId);
    if (result.success) setTriggers(result.triggers);
    setIsLoading(false);
  }, [workflowId]);

  useEffect(() => { void load(); }, [load]);

  const handleDisable = async (id: string, label: string) => {
    const result = await disableWebhookTrigger(id);
    if (result.success) {
      toast({ title: 'Webhook disabled', description: label });
      void load();
    } else {
      toast({ title: 'Kunde inte disable', description: result.error, variant: 'destructive' });
    }
  };

  if (!limits.canRunProcesses) {
    return (
      <div className="p-6 text-center text-xs text-muted-foreground">
        <Webhook className="w-8 h-8 mx-auto mb-2 opacity-40" />
        Webhooks kräver Enterprise-plan.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="webhook-triggers-panel">
      <div className="flex items-center justify-between p-3 border-b">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Webhook className="w-4 h-4" />
            Webhook-triggers
          </h3>
          <p className="text-xs text-muted-foreground">
            Externa system POSTar hit för att trigga workflow (HMAC-signed)
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={isLoading}>
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="webhook-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />
            Ny webhook
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-2 space-y-2">
        {isLoading && triggers.length === 0 && (
          <div className="text-center py-6 text-xs text-muted-foreground">Laddar…</div>
        )}
        {!isLoading && triggers.length === 0 && (
          <div className="text-center py-6 text-xs text-muted-foreground">
            Inga webhooks ännu. Klicka "Ny webhook" för att skapa en.
          </div>
        )}
        {triggers.map((trigger) => {
          const disabled = trigger.disabled_at !== null;
          return (
            <div
              key={trigger.id}
              className={`p-2 rounded-md border ${disabled ? 'bg-muted/30 opacity-60' : 'bg-card'}`}
              data-testid={`webhook-row-${trigger.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-xs flex-1 truncate">{trigger.label}</span>
                {disabled && <Badge variant="outline" className="text-[9px]">Disabled</Badge>}
                <WebhookUrlCopyButton path={trigger.path} />
                {!disabled && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Disable "{trigger.label}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Externa system som POSTar till denna webhook-URL kommer få 404.
                          Detta påverkar inte existerande historik — du kan skapa en ny webhook
                          med ny URL när du vill.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction onClick={() => void handleDisable(trigger.id, trigger.label)}>
                          Ja, disable
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px] text-muted-foreground">
                <div>Triggers: {trigger.trigger_count}</div>
                <div>Senast: {formatDate(trigger.last_triggered_at)}</div>
                <div className="col-span-2 font-mono truncate" title={trigger.path}>
                  /functions/v1/workflow-webhook/{trigger.path}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <CreateWebhookDialog
        open={createOpen}
        workflowId={workflowId}
        onClose={() => setCreateOpen(false)}
        onCreated={(path, secret, label) => {
          setNewWebhook({ url: buildWebhookUrl(path), secret, label });
          setCreateOpen(false);
          void load();
        }}
      />

      <NewWebhookDialog
        open={!!newWebhook}
        webhook={newWebhook}
        onClose={() => setNewWebhook(null)}
      />
    </div>
  );
}

function WebhookUrlCopyButton({ path }: { path: string }) {
  const [copied, setCopied] = useState(false);
  const url = buildWebhookUrl(path);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button
      size="icon"
      variant="ghost"
      className="h-6 w-6"
      onClick={() => void handleCopy()}
      title="Kopiera URL"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
}

function CreateWebhookDialog({
  open, workflowId, onClose, onCreated,
}: {
  open: boolean;
  workflowId: string;
  onClose: () => void;
  onCreated: (path: string, secret: string, label: string) => void;
}) {
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!label.trim()) return;
    setSubmitting(true);
    const result = await createWebhookTrigger(workflowId, label.trim());
    setSubmitting(false);
    if (result.success && result.path && result.signing_secret) {
      onCreated(result.path, result.signing_secret, result.label ?? label.trim());
      setLabel('');
    } else {
      toast({
        title: 'Kunde inte skapa webhook',
        description: result.error === 'enterprise-required'
          ? 'Kräver Enterprise-plan'
          : result.error,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Skapa ny webhook-trigger</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="webhook-label">Label</Label>
            <Input
              id="webhook-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="t.ex. 'Stripe payment.succeeded'"
              maxLength={100}
              data-testid="webhook-label-input"
            />
          </div>
          <div className="text-[10px] text-muted-foreground bg-muted/30 p-2 rounded-md">
            Efter creation får du en URL + signing_secret som externa system
            använder för att signera sina POSTs med HMAC-SHA256.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Avbryt</Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={!label.trim() || submitting}
              data-testid="webhook-submit-btn"
            >
              {submitting ? 'Skapar…' : 'Skapa webhook'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewWebhookDialog({
  open, webhook, onClose,
}: {
  open: boolean;
  webhook: { url: string; secret: string; label: string } | null;
  onClose: () => void;
}) {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  if (!webhook) return null;

  const copy = async (text: string, which: 'url' | 'secret') => {
    await navigator.clipboard.writeText(text);
    if (which === 'url') {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } else {
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Webhook skapad: "{webhook.label}"
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs">
            <strong>Viktigt:</strong> Signing_secret visas bara denna gång. Kopiera det NU.
            URL:en kan du hämta igen från listan.
          </div>
          <div>
            <Label>Webhook-URL (POST hit)</Label>
            <div className="flex gap-2 mt-1">
              <Input readOnly value={webhook.url} className="font-mono text-xs" />
              <Button onClick={() => void copy(webhook.url, 'url')}>
                {copiedUrl ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div>
            <Label>Signing secret (för HMAC-SHA256)</Label>
            <div className="flex gap-2 mt-1">
              <Input readOnly value={webhook.secret} className="font-mono text-xs" data-testid="webhook-secret-display" />
              <Button onClick={() => void copy(webhook.secret, 'secret')} data-testid="webhook-secret-copy">
                {copiedSecret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Signera body med secret via HMAC-SHA256 och skicka som header:
            <code className="font-mono bg-muted px-1 ml-1">X-Webhook-Signature: sha256=&lt;hex&gt;</code>
          </div>
          <Button onClick={onClose} className="w-full">
            Jag har sparat secret
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
